import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Search } from 'lucide-react';
import { buildProductPath } from '@/lib/product-url';
import {
  buildProductSeoTitle,
  buildProductSeoDescription,
  resolveProductSeoTitle,
  resolveProductSeoDescription,
} from '@/lib/product-seo';
import { getPublicSiteUrl } from '@/lib/seo';
import ProductVariantsEditor, { buildVariantsPayload } from '@/components/products/ProductVariantsEditor';

export function initProductEditForm(product) {
  if (!product) return {};
  return {
    name: product.name || '',
    description: product.description || '',
    price: product.price ?? '',
    compare_price: product.compare_price || '',
    stock: product.stock ?? 0,
    category: product.category || '',
    category_id: product.category_id || '',
    brand: product.brand || '',
    sku: product.sku || '',
    is_active: product.is_active !== false,
    made_in_turkey: product.made_in_turkey || false,
    weight: product.weight || '',
    tax_rate: product.tax_rate || '',
    shipping_cost: product.shipping_cost ?? '',
    international_shipping_cost: product.international_shipping_cost ?? '',
    origin_country: product.origin_country || '',
    images: product.images || [],
    sizes: product.sizes || [],
    colors: product.colors || [],
    variants: Array.isArray(product.variants) ? product.variants.map((v) => ({ ...v })) : [],
    seo_title: product.seo_title?.trim() || buildProductSeoTitle(product),
    seo_description: product.seo_description?.trim() || buildProductSeoDescription(product, 'tr'),
  };
}

const shippingNum = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return undefined;
  const n = Number(raw);
  return Number.isNaN(n) ? undefined : n;
};

const shippingEqual = (formVal, prodVal) => {
  const a = shippingNum(formVal);
  const b = prodVal == null || prodVal === '' ? undefined : Number(prodVal);
  if (a === undefined && (b === undefined || Number.isNaN(b))) return true;
  if (a === undefined || b === undefined) return false;
  return Math.abs(a - b) < 1e-9;
};

/** Admin / bayi PATCH-PUT gövdesi: yalnızca değişen alanlar */
export function buildProductUpdatePayload(form, product) {
  const updateData = {};
  if (form.name !== product.name) updateData.name = form.name;
  if (form.description !== (product.description || '')) updateData.description = form.description;
  if (Number(form.price) !== product.price) updateData.price = Number(form.price);
  if (form.compare_price && Number(form.compare_price) !== product.compare_price) {
    updateData.compare_price = Number(form.compare_price);
  }
  if (!form.compare_price && product.compare_price) updateData.compare_price = null;
  if (Number(form.stock) !== product.stock) updateData.stock = Number(form.stock);
  const prevCid = product.category_id || '';
  const nextCid = form.category_id || '';
  if (nextCid !== prevCid) {
    updateData.category_id = nextCid || null;
  }
  if (form.brand !== (product.brand || '')) updateData.brand = form.brand;
  if (form.sku !== (product.sku || '')) updateData.sku = form.sku;
  if (form.is_active !== product.is_active) updateData.is_active = form.is_active;
  if (form.made_in_turkey !== !!product.made_in_turkey) updateData.made_in_turkey = form.made_in_turkey;
  if (form.weight && Number(form.weight) !== product.weight) updateData.weight = Number(form.weight);
  if (!form.weight && product.weight) updateData.weight = null;
  if (form.tax_rate && Number(form.tax_rate) !== product.tax_rate) updateData.tax_rate = Number(form.tax_rate);
  if (!form.tax_rate && product.tax_rate) updateData.tax_rate = null;
  if (!shippingEqual(form.shipping_cost, product.shipping_cost)) {
    const v = shippingNum(form.shipping_cost);
    updateData.shipping_cost = v === undefined ? null : v;
  }
  if (!shippingEqual(form.international_shipping_cost, product.international_shipping_cost)) {
    const v = shippingNum(form.international_shipping_cost);
    updateData.international_shipping_cost = v === undefined ? null : v;
  }
  if (form.origin_country !== (product.origin_country || '')) updateData.origin_country = form.origin_country;
  if (JSON.stringify(form.images) !== JSON.stringify(product.images || [])) updateData.images = form.images;
  if (JSON.stringify(form.sizes || []) !== JSON.stringify(product.sizes || [])) updateData.sizes = form.sizes || [];
  if (JSON.stringify(form.colors || []) !== JSON.stringify(product.colors || [])) updateData.colors = form.colors || [];
  if (JSON.stringify(form.variants || []) !== JSON.stringify(product.variants || [])) {
    const variantFields = buildVariantsPayload({
      sizes: form.sizes,
      colors: form.colors,
      variants: form.variants,
    });
    updateData.sizes = variantFields.sizes;
    updateData.colors = variantFields.colors;
    updateData.variants = variantFields.variants;
    if (variantFields._computedStock != null) updateData.stock = variantFields._computedStock;
  }
  if (form.seo_title !== (product.seo_title || '')) {
    updateData.seo_title = form.seo_title.trim() || null;
  }
  if (form.seo_description !== (product.seo_description || '')) {
    updateData.seo_description = form.seo_description.trim() || null;
  }
  return updateData;
}

const ProductDetailEditForm = ({ form, setForm, product, categoryOptions = [], imageUploadTestId = 'edit-image-upload' }) => {
  const { t } = useTranslation();
  const update = (patch) => setForm({ ...form, ...patch });

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('store.logoTooLarge', 'Max 5MB'));
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/upload-product-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        update({ images: [...(form.images || []), data.url] });
        toast.success(t('seller.imageAdded', 'Gorsel eklendi'));
      } else {
        toast.error(t('seller.imageUploadFailed', 'Gorsel yuklenemedi'));
      }
    } catch {
      toast.error(t('seller.imageUploadError', 'Gorsel yuklenirken hata'));
    }
    e.target.value = '';
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs mb-2 block">{t('seller.images', 'Gorseller')}</Label>
        {form.images?.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-2">
            {form.images.map((img, i) => (
              <div key={i} className="relative group">
                <img
                  src={img?.startsWith('/uploads/') ? img.replace('/uploads/', '/api/uploads/') : img}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover border"
                />
                <button
                  type="button"
                  onClick={() => {
                    const imgs = [...form.images];
                    imgs.splice(i, 1);
                    update({ images: imgs });
                  }}
                  className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-image-${i}`}
                >
                  x
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 items-center">
          <Input
            type="file"
            accept="image/*"
            className="text-xs h-8"
            onChange={uploadImage}
            data-testid={imageUploadTestId}
          />
        </div>
      </div>

      <Separator />
      <div className="grid grid-cols-1 gap-3">
        <div>
          <Label className="text-xs">{t('seller.productName', 'Urun Adi')}</Label>
          <Input
            value={form.name}
            onChange={(e) => update({ name: e.target.value })}
            data-testid="edit-product-name"
          />
        </div>
        <div>
          <Label className="text-xs">{t('seller.description', 'Aciklama')}</Label>
          <textarea
            className="w-full min-h-[80px] rounded-md border px-3 py-2 text-sm resize-y"
            value={form.description}
            onChange={(e) => update({ description: e.target.value })}
            data-testid="edit-product-description"
          />
        </div>
      </div>

      <Separator />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">{t('seller.price', 'Fiyat')}</Label>
          <Input
            type="number"
            step="0.01"
            value={form.price}
            onChange={(e) => update({ price: e.target.value })}
            data-testid="edit-product-price"
          />
        </div>
        <div>
          <Label className="text-xs">{t('seller.comparePrice', 'Karsilastirma Fiyati')}</Label>
          <Input
            type="number"
            step="0.01"
            value={form.compare_price}
            onChange={(e) => update({ compare_price: e.target.value })}
            data-testid="edit-product-compare-price"
          />
        </div>
        <div>
          <Label className="text-xs">{t('seller.stock', 'Stok')}</Label>
          <Input
            type="number"
            value={form.stock}
            onChange={(e) => update({ stock: e.target.value })}
            data-testid="edit-product-stock"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">{t('seller.category', 'Kategori')}</Label>
          <Select
            value={form.category_id ? form.category_id : '__none__'}
            onValueChange={(v) => update({ category_id: v === '__none__' ? '' : v })}
          >
            <SelectTrigger className="h-9" data-testid="edit-product-category">
              <SelectValue placeholder={t('seller.selectCategory', 'Kategori secin')} />
            </SelectTrigger>
            <SelectContent className="max-h-[280px]">
              <SelectItem value="__none__">{t('seller.noCategory', '(Kategori yok)')}</SelectItem>
              {categoryOptions
                .slice()
                .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'tr'))
                .map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1">
            {t('seller.categoryAssignHint', 'Urunu bir katalog kategorisine atayin (filtreleme ve listeler icin)')}
          </p>
        </div>
        <div>
          <Label className="text-xs">{t('seller.brand', 'Marka')}</Label>
          <Input value={form.brand} onChange={(e) => update({ brand: e.target.value })} data-testid="edit-product-brand" />
        </div>
        <div>
          <Label className="text-xs">{t('seller.sku', 'SKU')}</Label>
          <Input value={form.sku} onChange={(e) => update({ sku: e.target.value })} data-testid="edit-product-sku" />
        </div>
        <div>
          <Label className="text-xs">{t('seller.taxRate', 'Vergi Orani')} (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={form.tax_rate}
            onChange={(e) => update({ tax_rate: e.target.value })}
            data-testid="edit-product-tax-rate"
          />
        </div>
        <div className="sm:col-span-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            {t('seller.shippingPerProduct', 'Kargo (urun bazli, TRY)')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('seller.shippingDomestic', 'Yurtici teslimat')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder={t('seller.shippingPlaceholder', 'Orn. 185 veya 0')}
                value={form.shipping_cost}
                onChange={(e) => update({ shipping_cost: e.target.value })}
                data-testid="edit-product-shipping-domestic"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                {t('seller.shippingHint', '0 = ucretsiz; bos = site varsayilani')}
              </p>
            </div>
            <div>
              <Label className="text-xs">{t('seller.shippingInternational', 'Yurtdisi teslimat')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder={t('seller.shippingPlaceholder', 'Orn. 250 veya 0')}
                value={form.international_shipping_cost}
                onChange={(e) => update({ international_shipping_cost: e.target.value })}
                data-testid="edit-product-shipping-intl"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                {t('seller.shippingHint', '0 = ucretsiz; bos = site varsayilani')}
              </p>
            </div>
          </div>
        </div>
        <div>
          <Label className="text-xs">{t('seller.weightKg', 'Agirlik (kg)')}</Label>
          <Input
            type="number"
            step="0.01"
            value={form.weight}
            onChange={(e) => update({ weight: e.target.value })}
            data-testid="edit-product-weight"
          />
        </div>
      </div>

      <Separator />
      <ProductVariantsEditor
        value={{
          sizes: form.sizes || [],
          colors: form.colors || [],
          variants: form.variants || [],
        }}
        onChange={({ sizes, colors, variants }) => update({ sizes, colors, variants })}
      />

      <Separator />
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Label className="text-sm font-medium">
            {t('seller.seoSection', 'SEO (Google / sosyal paylasim)')}
          </Label>
        </div>
        <div>
          <Label className="text-xs">{t('seller.seoTitle', 'SEO baslik (meta title)')}</Label>
          <Input
            placeholder={t('seller.seoTitlePlaceholder', 'Orn: Dubai Cikolatasi 200g | Turkish E-Market')}
            value={form.seo_title}
            onChange={(e) => update({ seo_title: e.target.value })}
            maxLength={70}
            data-testid="edit-product-seo-title"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            {t('seller.seoTitleHint', 'Bos birakilirsa')}:{' '}
            <strong>{buildProductSeoTitle(product || { name: form.name })}</strong>{' '}
            ({t('seller.seoTitleIdeal', 'ideal: 50–60 karakter')})
          </p>
        </div>
        <div>
          <Label className="text-xs">{t('seller.seoDescription', 'SEO aciklama (meta description)')}</Label>
          <textarea
            className="flex min-h-[72px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder={t(
              'seller.seoDescPlaceholder',
              "Orn: Ozel Antep fistikli Dubai Cikolatasi 200g. Turkish E-Market'ten guvenle siparis verin..."
            )}
            value={form.seo_description}
            onChange={(e) => update({ seo_description: e.target.value })}
            maxLength={160}
            data-testid="edit-product-seo-description"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            {t('seller.seoDescHint', 'Bos birakilirsa aciklama ozeti + siparis CTA kullanilir (ideal: 150–158 karakter)')}
          </p>
        </div>
        <div className="rounded-lg border bg-white dark:bg-background p-3 space-y-0.5 max-w-xl">
          <p className="text-[10px] text-muted-foreground mb-1.5">{t('seller.seoLivePreview', 'Canli onizleme')}</p>
          {(() => {
            const preview = {
              ...product,
              name: form.name,
              description: form.description,
              seo_title: form.seo_title,
              seo_description: form.seo_description,
            };
            const site = getPublicSiteUrl();
            const path = buildProductPath('tr', preview);
            return (
              <>
                <p className="text-[#1a0dab] dark:text-blue-400 text-base leading-snug line-clamp-2">
                  {resolveProductSeoTitle(preview)}
                </p>
                <p className="text-[#006621] dark:text-green-500 text-xs truncate">
                  {site ? `${site}${path}` : path}
                </p>
                <p className="text-sm text-[#545454] dark:text-muted-foreground leading-snug line-clamp-3 pt-0.5">
                  {resolveProductSeoDescription(preview, 'tr')}
                </p>
              </>
            );
          })()}
        </div>
      </div>

      <Separator />
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Switch
            checked={form.is_active}
            onCheckedChange={(v) => update({ is_active: v })}
            data-testid="edit-product-active"
          />
          <Label className="text-xs">{t('seller.active', 'Aktif')}</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={form.made_in_turkey}
            onCheckedChange={(v) => update({ made_in_turkey: v })}
            data-testid="edit-product-made-in-turkey"
          />
          <Label className="text-xs">{t('seller.madeInTurkeyBadge', 'Made in Turkiye')}</Label>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailEditForm;
